import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone } from "lucide-react";
import Seo, { SITE, abs, breadcrumbLd, faqLd, localBusinessLd } from "@/lib/seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { GUIDES, guideBySlug, guideUrl } from "@/constants/guides";
import { zoneBySlug, zoneUrl } from "@/constants/zones";

const fmt = (d) => new Date(d).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" });

export default function GuidePage() {
  const { slug } = useParams();
  const g = guideBySlug(slug);
  if (!g) return <Navigate to="/blog" replace />;

  const path = guideUrl(g.slug);
  const crumbs = [
    { to: "/", label: "Accueil" },
    { to: "/blog", label: "Blog" },
    { to: path, label: g.kicker },
  ];
  const zone = zoneBySlug(g.zone);
  const autres = GUIDES.filter((x) => x.slug !== g.slug).slice(0, 3);

  return (
    <div data-testid={`guide-page-${g.slug}`} className="pt-[100px] md:pt-[140px] pb-24">
      <Seo
        title={g.title}
        description={g.description}
        path={path}
        image={g.img}
        type="article"
        jsonLd={[
          localBusinessLd(),
          breadcrumbLd(crumbs),
          faqLd(g.faq),
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: g.h1,
            description: g.description,
            image: abs(g.img),
            datePublished: g.date,
            dateModified: g.date,
            inLanguage: "fr-FR",
            mainEntityOfPage: abs(path),
            author: { "@type": "Organization", name: SITE.name, url: SITE.url },
            publisher: {
              "@type": "Organization",
              name: SITE.name,
              logo: { "@type": "ImageObject", url: abs(SITE.logo) },
            },
          },
        ]}
      />

      <article className="wrap">
        <Breadcrumbs items={crumbs} />
        <div className="font-mono-caption text-bordeaux-700 mt-8 mb-4">{g.kicker} · {g.readTime} de lecture</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-anthracite max-w-4xl leading-[1.02]">{g.h1}</h1>
        <div className="mt-5 text-sm text-ink-soft">Publié le {fmt(g.date)} par RAMA DÉCO — Construction & Rénovation</div>

        <div className="mt-10 aspect-[21/9] overflow-hidden rounded-rama-md">
          <img src={g.img} alt={g.h1} className="w-full h-full object-cover" width="1600" height="686" />
        </div>

        <p className="mt-10 max-w-3xl font-display text-xl md:text-2xl text-anthracite leading-relaxed">{g.chapo}</p>

        <div className="mt-14 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 max-w-3xl">
            {/* Sommaire */}
            <nav aria-label="Sommaire" className="mb-12 p-6 bg-greige/40 rounded-rama-sm">
              <div className="font-mono-caption text-ink-soft mb-3">Sommaire</div>
              <ol className="space-y-2 text-sm">
                {g.sections.map((s, i) => (
                  <li key={i}>
                    <a href={`#s${i}`} className="text-anthracite link-underline">{i + 1}. {s.h2}</a>
                  </li>
                ))}
              </ol>
            </nav>

            {g.sections.map((s, i) => (
              <section key={i} id={`s${i}`} className="mb-12 scroll-mt-28">
                <h2 className="font-display text-2xl md:text-4xl tracking-tight text-anthracite leading-tight">{s.h2}</h2>
                {s.p?.map((p, k) => (
                  <p key={k} className="mt-5 text-base md:text-lg text-ink-soft leading-relaxed">{p}</p>
                ))}
                {s.list && (
                  <ul className="mt-6 space-y-3">
                    {s.list.map((li, k) => (
                      <li key={k} className="flex gap-4 text-base text-anthracite leading-relaxed">
                        <span className="font-mono-caption text-bordeaux-700 mt-1 shrink-0">{String(k + 1).padStart(2, "0")}</span>
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.p2?.map((p, k) => (
                  <p key={k} className="mt-5 text-base md:text-lg text-ink-soft leading-relaxed">{p}</p>
                ))}
              </section>
            ))}

            {/* FAQ */}
            <section className="mt-16">
              <h2 className="font-display text-2xl md:text-4xl tracking-tight text-anthracite">Questions fréquentes</h2>
              <div className="mt-6 divide-y divide-black/10 border-y border-black/10">
                {g.faq.map((f, i) => (
                  <details key={i} data-testid={`guide-faq-${i}`} className="group py-5">
                    <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                      <h3 className="font-display text-lg md:text-xl text-anthracite leading-snug">{f.q}</h3>
                      <span className="text-bordeaux-700 shrink-0 mt-1 transition-transform duration-300 group-open:rotate-45 text-xl leading-none">+</span>
                    </summary>
                    <p className="mt-3 text-sm md:text-base text-ink-soft leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          </div>

          {/* ASIDE */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="p-6 bg-anthracite text-ivory rounded-rama-md">
                <div className="font-mono-caption text-greige mb-3">Votre projet</div>
                <div className="font-display text-2xl leading-tight">Une estimation chiffrée en 5 minutes.</div>
                <p className="mt-3 text-sm text-white/70">Décrivez vos travaux avec vos mots. Vous obtenez une fourchette ventilée par poste.</p>
                <Link to="/estimation" data-testid="guide-cta-estimation" className="btn-primary justify-center w-full mt-5">Estimer <ArrowRight size={16} /></Link>
                <a href={`tel:${SITE.phone}`} className="btn-ghost btn-ghost-light justify-center w-full mt-3">
                  <Phone size={15} /> {SITE.phoneDisplay}
                </a>
              </div>

              {zone && (
                <div className="p-6 bg-white border border-black/10 rounded-rama-md">
                  <div className="font-mono-caption text-ink-soft mb-3">Zone concernée</div>
                  <Link to={zoneUrl(zone.slug)} data-testid="guide-link-zone" className="font-display text-xl text-anthracite hover:text-bordeaux-700 transition-colors">
                    Rénovation {zone.name} →
                  </Link>
                  <p className="mt-2 text-sm text-ink-soft">{zone.delai}. Devis et déplacement gratuits.</p>
                </div>
              )}

              <div className="p-6 bg-white border border-black/10 rounded-rama-md">
                <div className="font-mono-caption text-ink-soft mb-4">Autres guides</div>
                <ul className="space-y-4">
                  {autres.map((a) => (
                    <li key={a.slug}>
                      <Link to={guideUrl(a.slug)} data-testid={`guide-related-${a.slug}`} className="text-sm text-anthracite hover:text-bordeaux-700 transition-colors inline-flex gap-2">
                        <ArrowUpRight size={15} className="shrink-0 mt-0.5 text-bordeaux-700" /> {a.h1}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </article>
    </div>
  );
}
